/**
 * PageHeader — section hero mirrored from zworks-router-v2.
 * Use at the top of a top-level view to set the eyebrow + title + actions.
 * Export/print is opt-in via `enableExport` so callers that only supply a
 * Refresh action don't get an unsolicited print button (regression caught in
 * PR #77 review).
 */
import { Download, Plus, RefreshCw } from 'lucide-react';
import type { ReactNode } from 'react';
import { Button } from './button';

export function PageHeader({
  eyebrow,
  title,
  description,
  action,
  onRefresh,
  enableExport = false,
}: {
  eyebrow: string;
  title: string;
  description: string;
  action?: ReactNode;
  onRefresh?: () => void;
  enableExport?: boolean;
}) {
  return (
    <div className="mb-6 flex flex-col gap-4 md:flex-row md:items-start">
      <div className="flex-1">
        <div className="mb-2 text-[10px] font-bold uppercase tracking-[.14em] text-primary">{eyebrow}</div>
        <h1 className="text-3xl font-bold tracking-tight md:text-4xl">{title}</h1>
        <p className="mt-2 max-w-3xl text-sm leading-6 text-muted-foreground">{description}</p>
      </div>
      <div className="flex flex-wrap gap-2">
        {enableExport ? (
          <Button variant="secondary" type="button" onClick={() => window.print()}>
            <Download className="size-4" />
            Export / print
          </Button>
        ) : null}
        {onRefresh ? (
          <Button variant="secondary" type="button" onClick={onRefresh}>
            <RefreshCw className="size-4" />
            Refresh
          </Button>
        ) : null}
        {action}
      </div>
    </div>
  );
}

export function LoadingCards() {
  return (
    <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-4">
      {[1, 2, 3, 4].map((i) => (
        <div key={i} className="h-28 animate-pulse rounded-xl border border-border bg-card">
          <div className="m-5 h-3 w-24 rounded bg-muted" />
          <div className="m-5 h-7 w-32 rounded bg-muted" />
        </div>
      ))}
    </div>
  );
}

export function ErrorState({ message, retry }: { message: string; retry: () => void }) {
  return (
    <div className="rounded-xl border border-destructive/25 bg-destructive/5 p-8 text-center">
      <h3 className="font-semibold text-destructive">Unable to load data</h3>
      <p className="mt-2 text-sm text-muted-foreground">{message}</p>
      <Button className="mt-4" onClick={retry}>
        Try again
      </Button>
    </div>
  );
}

export function PrimaryAction({ label, onClick }: { label: string; onClick?: () => void }) {
  return (
    <Button onClick={onClick}>
      <Plus className="size-4" />
      {label}
    </Button>
  );
}
